
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingActions from '../../components/feature/FloatingActions';
import AnimatedSection from '../../components/base/AnimatedSection';

const CareersPage = () => {
  const [selectedJob, setSelectedJob] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    resume: '',
    coverLetter: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const benefits = [
    {
      icon: 'ri-heart-pulse-line',
      title: 'Health Insurance',
      description: 'Comprehensive medical, dental, and vision coverage for you and your family',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Competitive Salary',
      description: 'Industry-leading compensation packages with performance bonuses',
    },
    {
      icon: 'ri-time-line',
      title: 'Flexible Schedule',
      description: 'Work-life balance with flexible hours and remote work options',
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Professional Development',
      description: 'Continuous learning opportunities and career advancement programs',
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Paid Time Off',
      description: 'Generous vacation days, sick leave, and paid holidays',
    },
    {
      icon: 'ri-team-line',
      title: 'Team Culture',
      description: 'Collaborative environment with supportive colleagues and leadership',
    },
  ];

  const jobs = [
    {
      id: 1,
      title: 'Medical Laboratory Technologist',
      department: 'Laboratory Operations',
      location: 'New York, NY',
      type: 'Full-time',
      description: 'We are seeking an experienced Medical Laboratory Technologist to join our team. The ideal candidate will perform complex laboratory tests, maintain equipment, and ensure quality control standards.',
      requirements: [
        'Bachelor\'s degree in Medical Technology or related field',
        'ASCP or AMT certification required',
        '2+ years of clinical laboratory experience',
        'Strong knowledge of laboratory procedures and safety protocols',
        'Excellent attention to detail and analytical skills',
      ],
      responsibilities: [
        'Perform routine and specialized laboratory tests',
        'Operate and maintain laboratory equipment',
        'Ensure quality control and quality assurance procedures',
        'Document test results accurately and timely',
        'Collaborate with healthcare providers on test interpretation',
      ],
    },
    {
      id: 2,
      title: 'Dental Laboratory Technician',
      department: 'Dental Services',
      location: 'New York, NY',
      type: 'Full-time',
      description: 'Join our dental laboratory team as a skilled technician. You will create high-quality dental prosthetics including crowns, bridges, and dentures using advanced CAD/CAM technology.',
      requirements: [
        'Certified Dental Technician (CDT) preferred',
        '3+ years of dental laboratory experience',
        'Proficiency in CAD/CAM systems',
        'Knowledge of dental materials and techniques',
        'Strong attention to detail and craftsmanship',
      ],
      responsibilities: [
        'Fabricate dental prosthetics and appliances',
        'Operate CAD/CAM equipment and software',
        'Ensure quality and accuracy of dental work',
        'Maintain laboratory equipment and supplies',
        'Collaborate with dentists on case specifications',
      ],
    },
    {
      id: 3,
      title: 'Clinical Research Coordinator',
      department: 'Research & Development',
      location: 'New York, NY',
      type: 'Full-time',
      description: 'We are looking for a Clinical Research Coordinator to manage clinical trials and research projects. This role involves coordinating study activities, ensuring regulatory compliance, and managing data collection.',
      requirements: [
        'Bachelor\'s degree in Life Sciences or related field',
        'CCRP certification preferred',
        '2+ years of clinical research experience',
        'Knowledge of GCP and FDA regulations',
        'Strong organizational and communication skills',
      ],
      responsibilities: [
        'Coordinate clinical trial activities and timelines',
        'Ensure compliance with regulatory requirements',
        'Manage study documentation and data collection',
        'Recruit and screen study participants',
        'Communicate with sponsors and regulatory agencies',
      ],
    },
    {
      id: 4,
      title: 'Quality Assurance Specialist',
      department: 'Quality Control',
      location: 'New York, NY',
      type: 'Full-time',
      description: 'Seeking a Quality Assurance Specialist to maintain and improve our quality management system. You will conduct audits, implement quality initiatives, and ensure compliance with industry standards.',
      requirements: [
        'Bachelor\'s degree in relevant field',
        'ASQ certification preferred',
        '3+ years of QA experience in healthcare',
        'Knowledge of ISO standards and regulatory requirements',
        'Strong analytical and problem-solving skills',
      ],
      responsibilities: [
        'Conduct internal audits and inspections',
        'Develop and implement quality procedures',
        'Monitor compliance with quality standards',
        'Investigate quality issues and implement corrective actions',
        'Prepare for external audits and inspections',
      ],
    },
    {
      id: 5,
      title: 'Laboratory Manager',
      department: 'Management',
      location: 'New York, NY',
      type: 'Full-time',
      description: 'We are seeking an experienced Laboratory Manager to oversee daily operations, manage staff, and ensure efficient laboratory services. This leadership role requires strong management and technical skills.',
      requirements: [
        'Master\'s degree in Medical Technology or related field',
        'ASCP certification required',
        '5+ years of laboratory management experience',
        'Strong leadership and communication skills',
        'Knowledge of laboratory regulations and accreditation',
      ],
      responsibilities: [
        'Oversee laboratory operations and staff',
        'Ensure compliance with regulatory standards',
        'Manage budgets and resources',
        'Implement quality improvement initiatives',
        'Coordinate with healthcare providers and stakeholders',
      ],
    },
    {
      id: 6,
      title: 'Phlebotomist',
      department: 'Patient Services',
      location: 'New York, NY',
      type: 'Part-time',
      description: 'Join our patient services team as a Phlebotomist. You will collect blood samples, interact with patients, and ensure proper specimen handling and processing.',
      requirements: [
        'Phlebotomy certification required',
        '1+ year of phlebotomy experience',
        'Excellent patient interaction skills',
        'Knowledge of specimen collection procedures',
        'Ability to work flexible hours',
      ],
      responsibilities: [
        'Collect blood and other specimens from patients',
        'Verify patient identification and test orders',
        'Label and process specimens accurately',
        'Maintain phlebotomy supplies and equipment',
        'Provide excellent patient care and service',
      ],
    },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4m4tln7s74mkk8pdua0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: '',
          resume: '',
          coverLetter: '',
        });
        setSelectedJob(null);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FloatingActions />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/145e9794e76f7244c20c0c1cc05c860e.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 to-royal-900/70"></div>
        </div>

        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-5xl lg:text-6xl font-display font-bold mb-4">Join Our Team</h1>
          <p className="text-xl mb-6 max-w-2xl mx-auto">
            Build your career with a leader in laboratory medicine
          </p>
          <div className="flex items-center justify-center space-x-2 text-white/80">
            <Link to="/" className="hover:text-white transition-colors duration-300">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>Careers</span>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <AnimatedSection className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-6">
              Why Work With Us?
            </h2>
            <p className="text-lg text-gray-600">
              We offer competitive benefits and a supportive work environment
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-primary-500 to-royal-600 rounded-2xl mb-6">
                    <i className={`${benefit.icon} text-white text-3xl`}></i>
                  </div>
                  <h3 className="text-xl font-display font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Job Listings */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-6">
              Open Positions
            </h2>
            <p className="text-lg text-gray-600">
              Explore current opportunities and find your perfect role
            </p>
          </div>

          <div className="space-y-6">
            {jobs.map((job, index) => (
              <AnimatedSection key={job.id} delay={index * 0.05}>
                <div className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-primary-500 hover:shadow-xl transition-all duration-300">
                  <div className="p-6 lg:p-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                      <div className="flex-1">
                        <h3 className="text-2xl font-display font-bold text-gray-900 mb-2">{job.title}</h3>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-2">
                            <i className="ri-building-line text-primary-500"></i>
                            <span>{job.department}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <i className="ri-map-pin-line text-primary-500"></i>
                            <span>{job.location}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <i className="ri-time-line text-primary-500"></i>
                            <span>{job.type}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedJob(selectedJob === job.id ? null : job.id)}
                        className="px-8 py-3 bg-gradient-to-r from-primary-500 to-royal-600 text-white rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300 whitespace-nowrap"
                      >
                        {selectedJob === job.id ? 'Close Details' : 'View Details'}
                      </button>
                    </div>

                    <p className="text-gray-600 leading-relaxed">{job.description}</p>

                    {selectedJob === job.id && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="grid md:grid-cols-2 gap-8">
                          <div>
                            <h4 className="text-lg font-bold text-gray-900 mb-4">Requirements</h4>
                            <ul className="space-y-2">
                              {job.requirements.map((req, idx) => (
                                <li key={idx} className="flex items-start space-x-2 text-gray-600">
                                  <i className="ri-checkbox-circle-fill text-green-500 mt-1 flex-shrink-0"></i>
                                  <span>{req}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-gray-900 mb-4">Responsibilities</h4>
                            <ul className="space-y-2">
                              {job.responsibilities.map((resp, idx) => (
                                <li key={idx} className="flex items-start space-x-2 text-gray-600">
                                  <i className="ri-arrow-right-circle-fill text-primary-500 mt-1 flex-shrink-0"></i>
                                  <span>{resp}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <AnimatedSection className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-6">
              Apply Now
            </h2>
            <p className="text-lg text-gray-600">
              Submit your application and join our team of professionals
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
            <form onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm"
                      placeholder="+1 (234) 567-890"
                    />
                  </div>

                  <div>
                    <label htmlFor="position" className="block text-sm font-semibold text-gray-700 mb-2">
                      Position Applied For *
                    </label>
                    <select
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm cursor-pointer"
                    >
                      <option value="">Select a position</option>
                      {jobs.map((job) => (
                        <option key={job.id} value={job.title}>
                          {job.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="resume" className="block text-sm font-semibold text-gray-700 mb-2">
                    Resume/CV Information *
                  </label>
                  <input
                    type="text"
                    id="resume"
                    name="resume"
                    value={formData.resume}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm"
                    placeholder="Provide link to your resume or describe how you'll send it"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Note: You can send your resume via email after submitting this form
                  </p>
                </div>

                <div>
                  <label htmlFor="coverLetter" className="block text-sm font-semibold text-gray-700 mb-2">
                    Cover Letter *
                  </label>
                  <textarea
                    id="coverLetter"
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 resize-none text-sm"
                    placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                  ></textarea>
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.coverLetter.length}/500 characters
                  </div>
                </div>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center space-x-2 text-green-700">
                      <i className="ri-checkbox-circle-line text-xl"></i>
                      <span className="font-semibold">Application submitted successfully! We'll review your application and contact you soon.</span>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center space-x-2 text-red-700">
                      <i className="ri-error-warning-line text-xl"></i>
                      <span className="font-semibold">Something went wrong. Please try again.</span>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || submitStatus === 'success'}
                  className="w-full px-8 py-4 bg-gradient-to-r from-primary-500 to-royal-600 text-white rounded-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Submitting...' : submitStatus === 'success' ? 'Application Submitted!' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </AnimatedSection>

      <Footer />
    </div>
  );
};

export default CareersPage;
